import { ConfirmationDialog } from '@zorro/design-system/v2/Dialog';
import { Drawer } from '@zorro/design-system/v2/Drawer';
import { useState } from 'react';

import { useAnalytics } from '../Analytics';
import {
  CreateEmployeeForm,
  CreateEmployeeFormProps,
} from './CreateEmployeeForm';

type Props = Omit<CreateEmployeeFormProps, 'onSuccess' | 'onDirtyChange'> & {
  isOpen: boolean;
  onClose: () => void;
};

export const CreateEmployeeDrawer = ({
  isOpen,
  onClose,
  ...formProps
}: Props) => {
  const { sendEvent } = useAnalytics();
  const [isDirty, setIsDirty] = useState(false);
  const [isDiscardModalOpen, setIsDiscardModalOpen] = useState(false);
  const { employerId } = formProps;

  const closeAndReset = () => {
    setIsDirty(false);
    setIsDiscardModalOpen(false);
    onClose();
  };

  const handleDrawerClose = () => {
    // Mantine sends Escape to every open overlay, so the drawer also sees the dialog's Esc
    if (isDiscardModalOpen) {
      return;
    }
    if (!isDirty) {
      closeAndReset();
      return;
    }
    setIsDiscardModalOpen(true);
    void sendEvent('open_add_employee_form_discard_changes_modal', {
      employerId,
    });
  };

  const handleKeepEditing = () => {
    setIsDiscardModalOpen(false);
    void sendEvent(
      'click_keep_editing_add_employee_form_discard_changes_modal',
      { employerId }
    );
  };

  const handleDiscardChanges = () => {
    void sendEvent('discard_add_employee_form_changes', { employerId });
    closeAndReset();
  };

  return (
    <>
      <Drawer
        opened={isOpen}
        title="Add a new employee"
        onClose={handleDrawerClose}
      >
        <CreateEmployeeForm
          {...formProps}
          onSuccess={closeAndReset}
          onDirtyChange={setIsDirty}
        />
      </Drawer>
      <ConfirmationDialog
        isOpen={isDiscardModalOpen}
        onClose={() => setIsDiscardModalOpen(false)}
        title="Discard changes?"
        description="You've made changes to this form. If you close it, your changes won't be saved."
        confirmLabel="Keep editing"
        onConfirm={handleKeepEditing}
        cancelLabel="Discard changes"
        onCancel={handleDiscardChanges}
      />
    </>
  );
};
